import React from 'react';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import GestureOutlinedIcon from '@mui/icons-material/GestureOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const MapTargetPanel = ({
  canUseDrawAreaModes,
  canUseTargetingMethods,
  contactTypeCopy,
  commitCustomRadius,
  commitCustomTargetCount,
  customRadiusInputValue,
  customTargetCount,
  displayedAreaMode,
  displayedNearbyTargetMode,
  handleCustomRadiusChange,
  handleCustomRadiusKeyDown,
  handleCustomTargetCountChange,
  handleCustomTargetCountKeyDown,
  handleDrawAreaModeSelect,
  handleNearbyRadiusSelect,
  handleNearbyTargetModeSelect,
  handleTargetCountSelect,
  isCustomRadiusSelected,
  isCustomTargetCountSelected,
  isManualAreaModeValue,
  isRadiusEstimatePending,
  maximumRadiusMiles,
  maximumTargetCount,
  minimumRadiusMiles,
  minimumTargetCount,
  nearbyRadiusOptions,
  radiusMiles,
  targetCount,
  targetCountOptions,
}) => (
  <Box className="mapTargetPanel">
    <Box className="mapTargetPanelHeader">
      <Typography className="mapTargetPanelTitle">How should we find {contactTypeCopy.plural}?</Typography>
    </Box>
    <Box
      className={[
        canUseDrawAreaModes ? 'mapTargetModeButtons' : 'mapTargetModeButtons nearbyOnly',
        canUseTargetingMethods &&
        ((displayedAreaMode === 'radius' && displayedNearbyTargetMode) || isManualAreaModeValue(displayedAreaMode))
          ? 'hasLinkedSettings'
          : '',
      ].filter(Boolean).join(' ')}
    >
      <Box className="mapTargetOptionWrap">
        <button
          className={canUseTargetingMethods && displayedAreaMode === 'radius' && displayedNearbyTargetMode === 'homes' ? 'mapTargetModeButton selected' : 'mapTargetModeButton'}
          disabled={!canUseTargetingMethods}
          onClick={() => handleNearbyTargetModeSelect('homes', true)}
          type="button"
        >
          <HomeOutlinedIcon />
          <span className="mapTargetModeCopy">
            <strong>{contactTypeCopy.numberTitle}</strong>
            <small>The closest {contactTypeCopy.plural} around the starting address</small>
          </span>
        </button>
      </Box>
      <Box className="mapTargetOptionWrap">
        <button
          className={canUseTargetingMethods && displayedAreaMode === 'radius' && displayedNearbyTargetMode === 'radius' ? 'mapTargetModeButton selected' : 'mapTargetModeButton'}
          disabled={!canUseTargetingMethods}
          onClick={() => handleNearbyTargetModeSelect('radius', true)}
          type="button"
        >
          <RadioButtonUncheckedIcon />
          <span className="mapTargetModeCopy">
            <strong>Radius Distance</strong>
            <small>{contactTypeCopy.titlePlural} within a set radius around the starting address</small>
          </span>
        </button>
      </Box>
      {canUseDrawAreaModes && (
        <Box className="mapTargetOptionWrap">
          <button
            className={canUseTargetingMethods && isManualAreaModeValue(displayedAreaMode) ? 'mapTargetModeButton selected' : 'mapTargetModeButton'}
            disabled={!canUseTargetingMethods}
            onClick={handleDrawAreaModeSelect}
            type="button"
          >
            <GestureOutlinedIcon />
            <span className="mapTargetModeCopy">
              <strong>Draw Area</strong>
              <small>Outline your own boundary to find {contactTypeCopy.plural}</small>
            </span>
          </button>
        </Box>
      )}
      {canUseTargetingMethods && displayedAreaMode === 'radius' && displayedNearbyTargetMode === 'homes' && (
        <Box className="mapTargetValueDropdown homesTargetCountDropdown">
          {targetCountOptions.map((count) => (
            <button
              className={!customTargetCount && Number(targetCount) === count ? 'mapOptionButton selected' : 'mapOptionButton'}
              disabled={!canUseTargetingMethods}
              key={count}
              onClick={() => handleTargetCountSelect(count)}
              type="button"
            >
              {count}
            </button>
          ))}
          <TextField
            className={isCustomTargetCountSelected ? 'mapCustomInput selected' : 'mapCustomInput'}
            disabled={!canUseTargetingMethods}
            inputProps={{ max: maximumTargetCount, min: minimumTargetCount, step: 1 }}
            label="Custom value"
            onBlur={commitCustomTargetCount}
            onChange={handleCustomTargetCountChange}
            onFocus={() => handleNearbyTargetModeSelect('homes')}
            onKeyDown={handleCustomTargetCountKeyDown}
            size="small"
            type="number"
            value={customTargetCount}
          />
        </Box>
      )}
      {canUseTargetingMethods && displayedAreaMode === 'radius' && displayedNearbyTargetMode === 'radius' && (
        <Box className="mapTargetValueDropdown radiusTargetDistanceDropdown">
          {nearbyRadiusOptions.map((radius) => (
            <button
              className={customRadiusInputValue === '' && Number(radiusMiles) === radius ? 'mapOptionButton selected' : 'mapOptionButton'}
              disabled={!canUseTargetingMethods}
              key={radius}
              onClick={() => handleNearbyRadiusSelect(radius)}
              type="button"
            >
              {radius} miles
            </button>
          ))}
          <TextField
            className={isCustomRadiusSelected ? 'mapCustomInput selected' : 'mapCustomInput'}
            disabled={!canUseTargetingMethods}
            inputProps={{ max: maximumRadiusMiles, min: minimumRadiusMiles, step: 0.1 }}
            label={isRadiusEstimatePending ? 'Updating...' : 'Custom value'}
            onBlur={commitCustomRadius}
            onChange={handleCustomRadiusChange}
            onFocus={() => handleNearbyTargetModeSelect('radius')}
            onKeyDown={handleCustomRadiusKeyDown}
            size="small"
            type="number"
            value={customRadiusInputValue}
          />
        </Box>
      )}
    </Box>
  </Box>
);

export default MapTargetPanel;
